import {claimHost} from './internal/lifecycle';
import {installStyle} from './internal/style';
import {addClassNames, clamp01, finite, setParts} from './internal/dom';
import {componentSurfaceCss} from './internal/surface';
// ============================================================================
// Domain-neutral level / spectrum meter presenter.
//
// It owns DOM, styling, ARIA and animation-frame scheduling. The caller owns
// the binding and any audio graph behind it.
// ============================================================================

export interface MeterLevelState {
  /** Current normalized fill in [0, 1]. */
  level: number;
  /** Optional normalized instantaneous peak. */
  peak?: number;
  /** Optional normalized decaying peak marker. */
  peakHold?: number;
}

export interface MeterBinding {
  readLevel(): MeterLevelState;
  readSpectrum(bars: number): ArrayLike<number>;
}

export interface MeterClassNames {
  root?: string;
  track?: string;
  fill?: string;
  peak?: string;
  spectrum?: string;
  bar?: string;
}

export interface MeterParts {
  root?: string;
  track?: string;
  fill?: string;
  peak?: string;
  spectrum?: string;
  bar?: string;
}

export interface MeterOptions {
  mode?: 'level' | 'spectrum';
  bars?: number;
  label?: string;
  /** Start the animation loop. Defaults to true. */
  animate?: boolean;
  height?: number | string;
  color?: string;
  peakColor?: string;
  backgroundColor?: string;
  classNames?: MeterClassNames;
  parts?: MeterParts;
  onError?: (error: unknown) => void;
  /** Install the exported stylesheet into the host. Defaults to true. */
  stylesheet?: boolean;
}

export interface MeterHandle {
  element: HTMLElement;
  redraw(): void;
  /** Update the accessible name in place; `undefined` restores the mode default. */
  updateLabel(label?: string): void;
  destroy(): void;
}

type MeterHost = HTMLElement | ShadowRoot;

const mountedMeters = new WeakMap<MeterHost, MeterHandle>();

/** Semantic tokens first; legacy `--wameter-*` aliases remain supported. */
export const meterStyle = `
.wui-meter {
${componentSurfaceCss('meter', {
  padding: 'var(--wm-meter-padding, .6rem)',
  radius: 'var(--wm-meter-radius, var(--wm-control-radius, 0))',
  background: 'var(--wui-meter-background, var(--wameter-bg, var(--wm-meter-background, var(--wm-surface, #fff))))',
})}
  display: block;
  width: 100%;
  min-width: 0;
  height: var(--wui-meter-height, var(--wameter-height, var(--wm-meter-height, 48px)));
  overflow: hidden;
  color: var(--wui-meter-fill, var(--wameter-fill, var(--wm-meter-fill, var(--wm-accent, #999))));
}
.wui-meter, .wui-meter * { box-sizing: border-box; }
.wui-meter__track {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: max(0px, calc(var(--wm-meter-radius, var(--wm-control-radius, 0)) - 2px));
  background: var(--wameter-track, var(--wm-meter-track, var(--wm-surface-muted, #f3f3f3)));
}
.wui-meter__fill {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0;
  background: currentColor;
}
.wui-meter__peak {
  position: absolute;
  inset: 0 auto 0 0;
  width: 2px;
  transform: translateX(-100%);
  background: var(--wui-meter-peak, var(--wameter-peak, var(--wm-meter-peak, var(--wm-danger, #e0445b))));
}
.wui-meter__spectrum {
  display: flex;
  align-items: flex-end;
  gap: var(--wm-meter-gap, 2px);
  width: 100%;
  height: 100%;
}
.wui-meter__bar {
  flex: 1 1 0;
  min-width: 0;
  height: 1%;
  background: currentColor;
  transform-origin: bottom;
}
@media (forced-colors: active) {
  .wui-meter { border: 1px solid CanvasText; color: Highlight; }
  .wui-meter__peak { background: CanvasText; }
}
`;

function countBars(value: unknown): number {
  return Math.max(4, Math.floor(finite(value, 28)));
}

function cssLength(value: string | number): string {
  return typeof value === 'number' ? `${Math.max(0, value)}px` : value;
}

/** Mount a live, accessible meter into an element or open shadow root. */
export function mountMeter(
  host: MeterHost,
  binding: MeterBinding,
  options: MeterOptions = {},
): MeterHandle {

  const document = host.ownerDocument;
  const mode = options.mode === 'spectrum' ? 'spectrum' : 'level';
  const defaultLabel = mode === 'spectrum' ? 'Spectrum' : 'Audio level';
  const barCount = countBars(options.bars);
  const style = installStyle(document, 'meter', meterStyle, options.stylesheet);

  const root = document.createElement('div');
  root.className = 'wui-meter';
  root.setAttribute('role', 'meter');
  root.setAttribute('aria-label', options.label ?? defaultLabel);
  root.setAttribute('aria-valuemin', '0');
  root.setAttribute('aria-valuemax', '100');
  root.setAttribute('aria-valuenow', '0');
  addClassNames(root, options.classNames?.root);
  setParts(root, 'root', options.parts?.root);
  if (options.height !== undefined) root.style.setProperty('--wui-meter-height', cssLength(options.height));
  if (options.color !== undefined) root.style.setProperty('--wui-meter-fill', options.color);
  if (options.peakColor !== undefined) root.style.setProperty('--wui-meter-peak', options.peakColor);
  if (options.backgroundColor !== undefined) {
    root.style.setProperty('--wui-meter-background', options.backgroundColor);
  }

  let fill: HTMLElement | undefined;
  let peak: HTMLElement | undefined;
  let bars: HTMLElement[] = [];

  if (mode === 'spectrum') {
    const spectrum = document.createElement('div');
    spectrum.className = 'wui-meter__spectrum';
    addClassNames(spectrum, options.classNames?.spectrum);
    setParts(spectrum, 'spectrum', options.parts?.spectrum);
    bars = Array.from({length: barCount}, () => {
      const bar = document.createElement('div');
      bar.className = 'wui-meter__bar';
      addClassNames(bar, options.classNames?.bar);
      setParts(bar, 'bar', options.parts?.bar);
      spectrum.append(bar);
      return bar;
    });
    root.append(spectrum);
  } else {
    const track = document.createElement('div');
    track.className = 'wui-meter__track';
    addClassNames(track, options.classNames?.track);
    setParts(track, 'track', options.parts?.track);
    fill = document.createElement('div');
    fill.className = 'wui-meter__fill';
    addClassNames(fill, options.classNames?.fill);
    setParts(fill, 'fill', options.parts?.fill);
    peak = document.createElement('div');
    peak.className = 'wui-meter__peak';
    addClassNames(peak, options.classNames?.peak);
    setParts(peak, 'peak', options.parts?.peak);
    track.append(fill, peak);
    root.append(track);
  }


  let destroyed = false;
  let rafId: number | null = null;
  const reportError = (error: unknown): void => options.onError?.(error);

  const updateLabel = (label?: string): void => {
    if (destroyed) return;
    root.setAttribute('aria-label', label ?? defaultLabel);
  };

  const redraw = (): void => {
    if (destroyed) return;
    try {
      if (mode === 'spectrum') {
        const values = binding.readSpectrum(barCount);
        let maximum = 0;
        for (let index = 0; index < bars.length; index += 1) {
          const value = clamp01(values[index]);
          maximum = Math.max(maximum, value);
          bars[index]!.style.height = `${Math.max(1, value * 100)}%`;
        }
        const percentage = Math.round(maximum * 100);
        root.setAttribute('aria-valuenow', String(percentage));
        root.setAttribute('aria-valuetext', `${percentage}% spectrum peak`);
      } else {
        const state = binding.readLevel();
        const level = clamp01(state.level);
        const held = clamp01(state.peakHold ?? state.peak ?? level);
        fill!.style.width = `${level * 100}%`;
        peak!.style.left = `${held * 100}%`;
        const percentage = Math.round(level * 100);
        root.setAttribute('aria-valuenow', String(percentage));
        root.setAttribute('aria-valuetext', `${percentage}% level`);
      }
    } catch (error) {
      reportError(error);
    }
  };

  const requestFrame =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame.bind(globalThis)
      : undefined;
  const cancelFrame =
    typeof cancelAnimationFrame === 'function'
      ? cancelAnimationFrame.bind(globalThis)
      : undefined;
  const tick = (): void => {
    if (destroyed) return;
    redraw();
    if (destroyed) return;
    rafId = requestFrame?.(tick) ?? null;
  };
  if (options.animate === false) redraw();
  else if (requestFrame) rafId = requestFrame(tick);

  const handle: MeterHandle = {
    element: root,
    redraw,
    updateLabel,
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      let cleanupError: unknown;
      if (rafId != null) {
        try {
          cancelFrame?.(rafId);
        } catch (error) {
          cleanupError ??= error;
        }
      }
      rafId = null;
      try {
        root.remove();
      } catch (error) {
        cleanupError ??= error;
      }
      try {
        style?.remove();
      } catch (error) {
        cleanupError ??= error;
      }
      bars = [];
      fill = undefined;
      peak = undefined;
      claim.release();
      if (cleanupError !== undefined) reportError(cleanupError);
    },
  };
  // Claim the host before destroying the previous mount: its cleanup may mount
  // a replacement, and that replacement must win.
  const claim = claimHost(mountedMeters, host, handle);
  claim.destroyPrevious();
  if (!claim.isCurrent()) return handle;

  host.append(...(style ? [style] : []), root);
  return handle;
}
